/**
 * App-shell reference-data queries: the account and location selector lists
 * that App.tsx feeds into MainNavigation.
 *
 * These used to live as fetch-effects inside App.tsx whose dependency arrays
 * included the *selection* state (selectedAccountId / selectedLocationId), so
 * every auto-select and every account/location switch re-issued the fetches.
 * As queries they depend only on their real inputs; selection changes no
 * longer refetch anything — session mutations (switchAccount/switchLocation)
 * invalidate the affected keys instead.
 *
 * Logo URLs are NOT cache-busted here. They used to carry a `?t=` of the fetch
 * time, which changes on every refetch — a new URL each time, so the browser
 * cache and the endpoint's ETag were both bypassed and every logo in the
 * account and location lists re-downloaded whenever the list refreshed. The
 * logo endpoints send Cache-Control + ETag, and an actual logo change already
 * busts the cache at the point of upload (the uploading screen stamps its own
 * preview), so a stable URL is both correct and much cheaper.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminService } from '../services/auth';
import { SelectOption } from '@turn-connect/ui';
import { queryKeys } from './queryKeys';
import { accountSummariesQuery } from './adminReference';

/**
 * Every live account for a system admin (id + name, light /accounts/summaries,
 * A–Z): the app's account names and its starting account. It used to also page
 * through the executive portfolio to order and badge the top bar's account
 * picker by open work; the picker is gone, and so is that paging.
 */
export function useSystemAdminAccounts(enabled: boolean) {
  // The shared summaries query (adminReference), so the account context and
  // every picker read one cache entry with one staleTime.
  const listQuery = useQuery({ ...accountSummariesQuery(), enabled });

  const accounts = useMemo<SelectOption[] | undefined>(() => {
    const list = listQuery.data;
    if (list === undefined) return undefined;
    return [...list].sort((a, b) => a.name.localeCompare(b.name)).map((a) => ({ value: a.id, label: a.name || 'Account' }));
  }, [listQuery.data]);

  return { accounts, error: listQuery.error };
}

/**
 * Account selector options for non-system-admin users, mirroring the old
 * effect's three branches:
 *  - multiple JWT accounts → /auth/accounts (with active job count badges),
 *    falling back to JWT data when the API fails or returns nothing
 *  - locations spanning multiple accounts → resolve each account's name
 *  - single account → no list (selector hidden), only an initial selection
 *
 * `initialAccountId` is the auto-select target for whichever branch applies;
 * the caller applies it only when nothing is selected yet.
 */
export function useMultiAccountOptions(user: any, enabled: boolean) {
  const userAccounts: any[] = useMemo(() => (user as any)?.accounts || [], [user]);
  const hasMultipleJwtAccounts = userAccounts.length > 1;

  // Fallback branch: derive candidate account ids from location roles.
  const derivedAccountIds = useMemo(() => {
    if (hasMultipleJwtAccounts) return [] as string[];
    const userLocations: any[] = (user as any)?.locations || [];
    const ids = new Set<string>();
    userLocations.forEach((loc: any) => {
      if (loc.account_id) ids.add(loc.account_id);
    });
    return Array.from(ids);
  }, [user, hasMultipleJwtAccounts]);

  const myAccountsQuery = useQuery({
    queryKey: queryKeys.accounts.mine,
    queryFn: async (): Promise<SelectOption[]> => {
      try {
        const apiAccounts = await adminService.getMyAccounts();
        if (apiAccounts.length > 0) {
          return apiAccounts.map((acc: any) => ({
            value: acc.id,
            label: acc.name || 'Account',
            badge: acc.active_job_count > 0 ? String(acc.active_job_count) : undefined,
          }));
        }
      } catch {
        // Fall back to JWT data if the API fails
      }
      // Fallback: use JWT data without counts
      return userAccounts.map((acc: any) => ({
        value: acc.id || acc.account_id,
        label: acc.name || acc.account_name || 'Account',
      }));
    },
    enabled: enabled && hasMultipleJwtAccounts,
  });

  const accountNamesQuery = useQuery({
    queryKey: queryKeys.accounts.names(derivedAccountIds),
    queryFn: async (): Promise<SelectOption[]> => {
      const accountsArray: SelectOption[] = [];
      for (const accountId of derivedAccountIds) {
        try {
          const account = await adminService.getAccount(accountId);
          accountsArray.push({
            value: accountId,
            label: account?.name || account?.title || `Account`,
          });
        } catch (err) {
          // If we can't fetch, use placeholder
          accountsArray.push({ value: accountId, label: `Account` });
        }
      }
      return accountsArray;
    },
    enabled: enabled && !hasMultipleJwtAccounts && derivedAccountIds.length > 1,
  });

  const accounts = hasMultipleJwtAccounts ? myAccountsQuery.data : derivedAccountIds.length > 1 ? accountNamesQuery.data : undefined;

  const initialAccountId = useMemo<string | undefined>(() => {
    if (hasMultipleJwtAccounts) {
      return myAccountsQuery.data?.[0]?.value;
    }
    if (derivedAccountIds.length > 1) {
      const list = accountNamesQuery.data;
      if (!list || list.length === 0) return undefined;
      // Prefer user's primary account_id if available
      const primaryAccountId = (user as any)?.account_id;
      return primaryAccountId && list.some((a) => a.value === primaryAccountId) ? primaryAccountId : list[0].value;
    }
    // Single account user — still select the account but don't show a selector
    if (userAccounts.length === 1) {
      const singleAccount = userAccounts[0];
      return singleAccount.id || singleAccount.account_id || undefined;
    }
    return undefined;
  }, [hasMultipleJwtAccounts, derivedAccountIds.length, myAccountsQuery.data, accountNamesQuery.data, user, userAccounts]);

  return { accounts, initialAccountId };
}

export interface AccountLocationsData {
  /** Selector options sorted by active job count descending (business rule), each with its IANA timezone when set. */
  options: (SelectOption & { timezone?: string; hasLogo?: boolean })[];
  /**
   * First location in raw API order. The account-switch handler auto-selects
   * this one (the pre-query handler used the unsorted list), while the
   * initial-load auto-select uses options[0] (busiest first) — preserved
   * distinctly for behavior parity.
   */
  firstIdInApiOrder?: string;
}

/**
 * Shared queryKey/queryFn for an account's location selector list, usable by
 * both useAccountLocations and imperative queryClient.fetchQuery in the
 * account-switch handler (so handler + hook share a single fetch).
 */
export function accountLocationsQueryOptions(accountId: string) {
  return {
    queryKey: queryKeys.locations.byAccount(accountId),
    queryFn: async (): Promise<AccountLocationsData> => {
      const { locations: rawLocations, activeJobCounts } = await adminService.getLocationsWithCounts(accountId);
      const locationList = Array.isArray(rawLocations) ? rawLocations : (rawLocations as any)?.data || [];

      const locationTuples = locationList.map((l: any) => {
        const count = activeJobCounts?.[l.id] || 0;
        return {
          option: {
            value: l.id,
            label: l.name || 'Location',
            logoUrl: adminService.getLocationLogoUrl(l.id),
            badge: count > 0 ? String(count) : undefined,
            timezone: (l.timezone as string | undefined) || undefined,
            // An uploaded logo of its own: the header's LogoMark loads it only then.
            hasLogo: !!l.logo_url,
          },
          jobCount: count,
        };
      });

      // Sort by active job count descending
      locationTuples.sort((a: { jobCount: number }, b: { jobCount: number }) => b.jobCount - a.jobCount);

      return {
        options: locationTuples.map(({ option }: { option: SelectOption & { timezone?: string; hasLogo?: boolean } }) => option),
        firstIdInApiOrder: locationList[0]?.id,
      };
    },
  };
}

/** Location selector list for users with account-level access, keyed by account. */
export function useAccountLocations(accountId: string | null | undefined, enabled: boolean) {
  return useQuery({
    ...accountLocationsQueryOptions(accountId || ''),
    enabled: enabled && !!accountId,
  });
}

export interface MyLocationsData {
  /** All accessible locations (unfiltered), sorted by active job count descending. */
  options: (SelectOption & { accountId?: string })[];
  /**
   * True when /auth/my-locations returned nothing and the single option was
   * synthesized from the user's own location_id — that option must not be
   * filtered by selected account, and auto-select follows the fallback rules.
   */
  isFallback: boolean;
  /** account_id of the first location in raw API order (account auto-select fallback). */
  firstAccountId?: string;
}

/**
 * Location selector list for users with only location-level access
 * (/auth/my-locations, no locations:read permission needed). Fetches the
 * full set once; filtering by the selected account happens in the caller so
 * account switches don't refetch.
 */
export function useMyLocations(user: any, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.locations.mine,
    queryFn: async (): Promise<MyLocationsData> => {
      const myLocations = await adminService.getMyLocations();

      if (myLocations.length === 0) {
        // Fallback: if user has location_id but API returned nothing, create a single-item
        const userLocationId = (user as any)?.location_id;
        if (userLocationId) {
          const locationName = (user as any)?.location_name || (user as any)?.location?.name || 'My Location';
          return {
            options: [
              {
                value: userLocationId,
                label: locationName,
                logoUrl: adminService.getLocationLogoUrl(userLocationId),
              },
            ],
            isFallback: true,
          };
        }
        return { options: [], isFallback: false };
      }

      // Sort by active job count descending (matches admin path behavior)
      const sorted = [...myLocations].sort((a, b) => b.active_job_count - a.active_job_count);

      const options = sorted.map((loc) => ({
        value: loc.id,
        label: loc.name || `Location ${loc.id}`,
        logoUrl: adminService.getLocationLogoUrl(loc.id),
        accountId: loc.account_id,
        badge: loc.active_job_count > 0 ? String(loc.active_job_count) : undefined,
      }));

      return { options, isFallback: false, firstAccountId: myLocations[0]?.account_id };
    },
    enabled,
  });
}
